import React from 'react';
import {
    Flex,
    HStack,
    useToast,
    VStack,
    Box,
    IconButton,
    Tooltip,
    Input,
    Spinner,
    Text,
} from '@cad-arc';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { WorkspaceTemplateType } from './types';
import { WorkspacesResource, WorkspaceTemplateOption } from '@mark43/cad-api';
import { formatMonthDayYear } from '../common/helpers/utils';
import { useMutation } from '@tanstack/react-query';
import { alphabeticalStringComparator } from '../common/sorting/comparators';

function EditableText(props: {
    workspaceId: number;
    workspaceOption: WorkspaceTemplateOption;
    onChangeText: (workspaceId: number, text: string) => void;
    isLoading: boolean;
    workspaceOptions: WorkspaceTemplateOption[];
}): React.ReactElement {
    const [text, setText] = React.useState<string>(props.workspaceOption.label);
    const [isInvalid, setIsInvalid] = React.useState<boolean>(false);
    const [mode, setMode] = React.useState<'EDIT' | 'VIEW'>('VIEW');
    const handleSave = React.useMemo(() => {
        return () => {
            if (
                props.workspaceOptions.filter(
                    (otherWorkspaceOption) =>
                        otherWorkspaceOption.label === text &&
                        otherWorkspaceOption.value !== props.workspaceOption.value
                ).length > 0
            ) {
                setIsInvalid(true);
            } else {
                setIsInvalid(false);
                setMode('VIEW');
                props.onChangeText(props.workspaceId, text);
            }
        };
    }, [props, text]);
    return (
        <VStack
            spacing="half"
            alignItems="flex-start"
            textOverflow="ellipsis"
            overflow="hidden"
            width="100%"
        >
            <HStack paddingRight={3} flexGrow={10} alignItems="center" width="100%">
                {mode === 'VIEW' ? (
                    <Box
                        borderRadius="base"
                        flexShrink={1}
                        _hover={{
                            backgroundColor: 'var(--arc-colors-interactive-hover)',
                        }}
                        overflow="hidden"
                        padding={1}
                        cursor="pointer"
                        onClick={() => {
                            setMode('EDIT');
                        }}
                    >
                        <Text
                            noOfLines={1}
                            style={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {text}
                        </Text>
                    </Box>
                ) : (
                    <Input
                        autoFocus
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onBlur={() => {
                            handleSave();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSave();
                            }
                        }}
                    />
                )}
                {props.isLoading ? <Spinner size="sm" /> : undefined}
            </HStack>
            {isInvalid ? (
                <Text variant="caption" color="negative">
                    Name in use
                </Text>
            ) : undefined}
        </VStack>
    );
}

function WorkspaceTemplateItem(props: {
    onRename?: (workspaceTemplateOption: WorkspaceTemplateOption) => void;
    workspaceOptions: WorkspaceTemplateOption[];
    defaultDepartmentTemplate?: WorkspaceTemplateOption;
    workspaceOption: WorkspaceTemplateOption;
    setWorkspaceOptions: React.Dispatch<React.SetStateAction<WorkspaceTemplateOption[]>>;
    workspaceTemplateType: WorkspaceTemplateType;
}): React.ReactElement {
    const apiClient = useApiClient();
    const toast = useToast();
    const deleteWorkspace = React.useCallback(
        (workspaceId: number) => {
            return props.workspaceTemplateType === 'USER'
                ? apiClient.request<WorkspacesResource.DeleteUserWorkspaceById>({
                      method: 'DELETE',
                      url: `workspaces/${workspaceId}/`,
                  })
                : apiClient.request<WorkspacesResource.DeleteDepartmentWorkspaceById>({
                      method: 'DELETE',
                      url: `workspaces/department/${workspaceId}/`,
                  });
        },
        [apiClient, props.workspaceTemplateType]
    );
    const onChangeTextMutation = useMutation(
        (args: { workspaceId: number; templateName: string }) =>
            props.workspaceTemplateType === 'USER'
                ? apiClient.request<WorkspacesResource.RenameUserWorkspaceTemplate>({
                      method: 'POST',
                      url: `workspaces/rename/template/${args.workspaceId}/`,
                      data: {
                          templateName: args.templateName,
                      },
                  })
                : apiClient.request<WorkspacesResource.RenameDepartmentWorkspaceTemplate>({
                      method: 'POST',
                      url: `workspaces/department/rename/template/${args.workspaceId}/`,
                      data: {
                          templateName: args.templateName,
                      },
                  }),
        {
            onSuccess(response) {
                const renamedWorkspaceOption = response.data.data;
                if (renamedWorkspaceOption) {
                    props.setWorkspaceOptions((currentWorkspaceOptions) => {
                        return currentWorkspaceOptions
                            .filter(
                                (workspaceOption) =>
                                    workspaceOption.value !== renamedWorkspaceOption.value
                            )
                            .concat([renamedWorkspaceOption])
                            .sort((a, b) => (a.value < b.value ? -1 : 1));
                    });
                    props.onRename?.(renamedWorkspaceOption);
                }
            },
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',
                    description: 'Failed to rename workspace.',
                });
            }),
        }
    );

    return (
        <Flex
            marginBottom={1}
            key={props.workspaceOption.value}
            justifyContent="space-between"
            alignItems="center"
            px={2}
        >
            <EditableText
                isLoading={onChangeTextMutation.isLoading}
                workspaceOption={props.workspaceOption}
                workspaceId={props.workspaceOption.value}
                workspaceOptions={props.workspaceOptions}
                onChangeText={(workspaceId, text) => {
                    onChangeTextMutation.mutate({
                        workspaceId,
                        templateName: text,
                    });
                }}
            />
            <HStack flexShrink={0} flexGrow={0}>
                <Text variant="caption">{`Modified on ${formatMonthDayYear(
                    props.workspaceOption.lastAccessedDateUtc
                )}`}</Text>
                <Tooltip
                    content={
                        props.workspaceOption.value === props.defaultDepartmentTemplate?.value
                            ? 'Cannot delete default department workspace.'
                            : undefined
                    }
                >
                    <IconButton
                        icon="TrashCan"
                        variant="ghost"
                        aria-label="delete workspace template"
                        disabled={
                            props.workspaceOption.value === props.defaultDepartmentTemplate?.value
                        }
                        onClick={() => {
                            deleteWorkspace(props.workspaceOption.value)
                                .then(() => {
                                    props.setWorkspaceOptions(
                                        props.workspaceOptions.filter(
                                            (option) => props.workspaceOption.value !== option.value
                                        )
                                    );
                                })
                                .catch(() => {
                                    toast({
                                        status: 'error',
                                        description: 'Failed to delete workspace.',
                                    });
                                });
                        }}
                        data-testid={`${props.workspaceOption.label}-delete-button`}
                    />
                </Tooltip>
            </HStack>
        </Flex>
    );
}

export function ManageWorkspaceList(props: {
    onRename?: (workspaceTemplateOption: WorkspaceTemplateOption) => void;
    defaultDepartmentTemplate?: WorkspaceTemplateOption;
    workspaceOptions: WorkspaceTemplateOption[];
    setWorkspaceOptions: React.Dispatch<React.SetStateAction<WorkspaceTemplateOption[]>>;
    workspaceTemplateType: WorkspaceTemplateType;
}): React.ReactElement {
    const sortedWorkspaceOptions = React.useMemo(() => {
        return props.workspaceOptions.slice().sort((left, right) => {
            return alphabeticalStringComparator(left.label, right.label);
        });
    }, [props.workspaceOptions]);
    return (
        <Box marginTop={3}>
            {sortedWorkspaceOptions.map((workspaceOption) => (
                <WorkspaceTemplateItem
                    key={workspaceOption.value}
                    workspaceOption={workspaceOption}
                    setWorkspaceOptions={props.setWorkspaceOptions}
                    workspaceTemplateType={props.workspaceTemplateType}
                    onRename={props.onRename}
                    defaultDepartmentTemplate={props.defaultDepartmentTemplate}
                    workspaceOptions={props.workspaceOptions}
                />
            ))}
        </Box>
    );
}
